import { CalendarBlank, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Loader, Toggle, VText } from '@vake/ui-kit';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { DetectionImage } from '../shared/components/Popup';
import { apiBaseUrl } from '../utils/env';

export function Information({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={clsx('flex p-2 bg-background-secondary rounded', className)}>{children}</div>;
}

export type Candidate = {
    timestamp: string;
    matching_label: string;
    lat: number;
    lon: number;
    image_source: string;
    est_length: number;
    mmsi: number;
    uuid: string;
    speed?: number;
};

export const CandidateList: FC<{
    CandidatePositions: Candidate[] | undefined;
    clickedBoatIndex: number | null;
    confidenceList: number[];
    isLoading: boolean;
    candidatesError: string | null;
    isAoiQueryed: boolean;
    isDateQueryed: boolean;
    setHoveredListBoatElement: (value: number | null) => void;
}> = ({
    CandidatePositions,
    clickedBoatIndex, // This didn't do anything in the old clearview either
    confidenceList,
    isLoading,
    candidatesError,
    isAoiQueryed,
    isDateQueryed,
    setHoveredListBoatElement,
}) => {
    const [showMatched, toggleShowMatched] = useState<boolean>(false);
    const [showConfidence, toggleShowConfidence] = useState<boolean>(true);
    return isLoading || CandidatePositions || candidatesError ? (
        <div className="absolute z-10 top-12 right-2 h-[85%] overflow-y-scroll">
            {CandidatePositions ? (
                CandidatePositions.length > 0 ? (
                    <div className="bg-background-primary rounded">
                        <div className="flex p-2 gap-2 items-center justify-between">
                            <div className="flex p-2 gap-2 items-center">
                                <VText>Show matched</VText>
                                <Toggle
                                    isOn={showMatched}
                                    onChange={() => toggleShowMatched(!showMatched)}
                                    offIcon={<XCircle />}
                                    onIcon={<CheckCircle />}
                                    onBackgroundColor="warning-default"
                                ></Toggle>
                            </div>
                            <div className="flex p-2 gap-2 items-center">
                                <VText>Show Confidence</VText>
                                <Toggle
                                    isOn={showConfidence}
                                    onChange={() => toggleShowConfidence(!showConfidence)}
                                    offIcon={<XCircle />}
                                    onIcon={<CheckCircle />}
                                    onBackgroundColor="warning-default"
                                ></Toggle>
                            </div>
                        </div>
                        <div>
                            {CandidatePositions.map((item, index) => {
                                let conf = 0;
                                if (index < confidenceList.length) {
                                    conf = confidenceList[index];
                                }
                                if (showMatched && item.matching_label === 'matched') {
                                    return undefined;
                                }
                                return (
                                    <div
                                        className="flex items-center self-start rounded border-2 border-background-primary hover:border-warning-default"
                                        key={index}
                                        onMouseEnter={() => setHoveredListBoatElement(index)}
                                        onMouseLeave={() => setHoveredListBoatElement(null)}
                                    >
                                        <div className="w-[300px]">
                                            <DetectionImage
                                                key={index}
                                                image_path={
                                                    apiBaseUrl() + '/insights/image/sentinel2/' + item.uuid + '.png'
                                                }
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            {typeof item.image_source === 'string' ? (
                                                <Information>
                                                    <img src="/images/icons/satellite.svg" alt="no source" />
                                                    <VText>: {item.image_source}</VText>
                                                </Information>
                                            ) : null}
                                            {typeof item.matching_label === 'string' ? (
                                                <>
                                                    <Information>
                                                        <VText>Status: {item.matching_label}</VText>
                                                    </Information>
                                                    {item.mmsi !== 0 ? (
                                                        <Information>
                                                            <VText>MMSI: {item.mmsi}</VText>
                                                        </Information>
                                                    ) : null}
                                                </>
                                            ) : null}

                                            {typeof item.speed === 'number' ? (
                                                <Information>
                                                    <VText>AIS Speed: {item.speed} kn</VText>
                                                </Information>
                                            ) : null}
                                            {typeof item.timestamp === 'string' ? (
                                                <Information>
                                                    <CalendarBlank color="white" />
                                                    <VText>
                                                        {item.timestamp.slice(0, 10)}{' '}
                                                        {item.timestamp?.slice(11, 16) + ' GMT'}
                                                    </VText>
                                                </Information>
                                            ) : null}
                                            {typeof item.est_length === 'number' ? (
                                                <Information>
                                                    <VText>
                                                        Estimated length: {Math.floor(item.est_length * 10) / 10} m
                                                    </VText>
                                                </Information>
                                            ) : null}
                                            {showConfidence && conf !== 0 ? (
                                                <Information className="bg-warning-default">
                                                    <VText significance="quaternary">Confidence: {conf}%</VText>
                                                </Information>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <ClearViewErrorElement>
                        <VText>No data found for provided {isDateQueryed && 'time '}</VText>
                        <VText>
                            {isDateQueryed && isAoiQueryed && 'and '} {isAoiQueryed && 'area '} input
                        </VText>
                    </ClearViewErrorElement>
                )
            ) : (
                <Loader size={32} />
            )}
        </div>
    ) : null;
};

const ClearViewErrorElement = ({ children }: { children: React.ReactNode }) => {
    return <div className="bg-background-primary rounded w-[400px] h-[200px]">{children}</div>;
};
